import { z } from "zod";
import {
  aiCitationTypeSchema,
  checkVerdictSchema,
  confidenceLevelSchema,
  evidenceTypeSchema,
  riskStatusSchema,
  severitySchema,
} from "./enums.js";

/**
 * Schema the AI review must conform to.
 *
 * Validation is deliberately split in two: the envelope (overall risk, summary,
 * material overview, checklist) is parsed independently of the findings, and
 * each finding is then parsed on its own. One malformed finding therefore costs
 * that finding rather than the whole review, which previously meant a requester
 * saw a generic "analysis unavailable" message even when the model had produced
 * a usable summary.
 *
 * A finding may cite only reference ids the server supplied with the prompt.
 * Unknown ids are rejected. A finding with no citations at all is kept but
 * marked advisory, because refusing it entirely leaves an organization with no
 * approved sources unable to receive any result.
 */

export const aiCitationSchema = z.object({
  /** Must be one of the reference ids provided to the model in the prompt. */
  referenceId: z.string().min(1),
  type: aiCitationTypeSchema,
  /** Short human-readable locator, e.g. clause or section. Optional. */
  locator: z.string().max(300).optional(),
});
export type AiCitation = z.infer<typeof aiCitationSchema>;

/**
 * A box expressed relative to the rendered image or PDF page. The origin is
 * the top-left corner. Keeping this normalized makes the same location usable
 * at every preview size without baking annotations into the source file.
 */
export const normalizedRegionSchema = z
  .object({
    x: z.number().finite().min(0).max(1),
    y: z.number().finite().min(0).max(1),
    width: z.number().finite().gt(0).max(1),
    height: z.number().finite().gt(0).max(1),
  })
  .superRefine((value, ctx) => {
    if (value.x + value.width > 1) {
      ctx.addIssue({ code: "custom", path: ["width"], message: "Region exceeds the right edge." });
    }
    if (value.y + value.height > 1) {
      ctx.addIssue({ code: "custom", path: ["height"], message: "Region exceeds the bottom edge." });
    }
  });
export type NormalizedRegion = z.infer<typeof normalizedRegionSchema>;

export const aiFindingSchema = z.object({
  /** Stable id assigned by the model so we can dedupe within a run. */
  id: z.string().min(1),
  title: z.string().min(1).max(300),
  /** Why this matters, in Korean. */
  explanation: z.string().min(1).max(4000),
  severity: severitySchema,
  confidence: confidenceLevelSchema,
  /**
   * For TEXT evidence, the exact problematic phrase copied verbatim from the
   * material. For VISUAL evidence, a description of what is visible and where.
   */
  evidenceQuote: z.string().min(1).max(2000),
  evidenceType: evidenceTypeSchema.default("TEXT"),
  /** One-based page or slide number when the evidence comes from an attached file. */
  pageNumber: z.number().int().positive().optional(),
  /** Normalized PDF/image coordinates (0..1) when available. */
  region: normalizedRegionSchema.optional(),
  /** Proposed compliant rewrite in Korean. Required above LOW severity. */
  suggestedRevision: z.string().max(4000).optional(),
  /**
   * Citations to allow-listed references. May be empty: the server then records
   * the finding as advisory with no authoritative source.
   */
  citations: z.array(aiCitationSchema).default([]),
});
export type AiFinding = z.infer<typeof aiFindingSchema>;

/** A per-reference verdict, so the requester sees what was actually checked. */
export const aiCheckedItemSchema = z.object({
  referenceId: z.string().min(1),
  title: z.string().max(300).optional(),
  verdict: checkVerdictSchema,
  note: z.string().max(1000).optional(),
});
export type AiCheckedItem = z.infer<typeof aiCheckedItemSchema>;

/**
 * The response envelope. `findings` is intentionally untyped here so that a
 * single malformed finding cannot invalidate the summary and checklist.
 */
export const aiReviewEnvelopeSchema = z.object({
  /** The model's proposed overall risk. Server may escalate, never relax to PASS. */
  overallRisk: riskStatusSchema,
  summary: z.string().min(1).max(4000),
  /**
   * What the model understood the material to be. Required in the prompt for
   * file-only submissions, where the requester otherwise has no way to tell
   * whether the attachment was read at all.
   */
  materialOverview: z.string().max(4000).optional(),
  /** Plain-language next step for the requester. */
  recommendedAction: z.string().max(600).optional(),
  checkedItems: z.array(aiCheckedItemSchema).default([]),
  findings: z.array(z.unknown()).default([]),
});
export type AiReviewEnvelope = z.infer<typeof aiReviewEnvelopeSchema>;

/** The envelope after each finding has been individually validated. */
export const aiReviewResultSchema = aiReviewEnvelopeSchema.extend({
  findings: z.array(aiFindingSchema),
});
export type AiReviewResult = z.infer<typeof aiReviewResultSchema>;

/** A reference the server exposes to the model as a citable source. */
export const aiReferenceSchema = z.object({
  referenceId: z.string().min(1),
  type: aiCitationTypeSchema,
  title: z.string(),
  /** Short description / applicable text the model can reason over. */
  content: z.string(),
});
export type AiReference = z.infer<typeof aiReferenceSchema>;
